<script setup>
import { ref, onBeforeMount, computed } from "vue"
import { useRoute, useRouter } from "vue-router"
import { createToaster } from "@meforma/vue-toaster"

import NavigationBar from "@/components/NavigationBar.vue"
import ConnectionStage from "@/pages/CreateConnection/ConnectionStage.vue"
import SelectFileType from "@/pages/CreateConnection/SelectFileType.vue"
import FilesProcessor from "@/components/Connection/FilesProcessor.vue"
import FileProcessor from "@/components/Connection/FileProcessor.vue"
import SelectDataProcessorType from "@/pages/CreateConnection/SelectDataProcessorType.vue"
import FilterProcessor from "@/components/Connection/FilterProcessor.vue"
import YandexDirectStatProcessor from "@/components/YandexDirectStatistics/YandexDirectStatProcessor.vue"
import VKAdsStatProcessor from "@/components/VKAdwordsStatistics/VKAdsStatProcessor.vue"
import GoogleSheetSelectModal from "@/components/GoogleModal/GoogleSheetSelectModal.vue"
import BackSvg from "@/components/UI/svg/BackSvg.vue"
import LoadingComponent from "@/components/UI/LoadingComponent.vue"

import { Api } from "@/service/api"

import { useGoogleSheets } from "@/pages/CreateConnection/composables/useGoogleSheets"
import { useYandexStatistics } from "@/pages/CreateConnection/composables/useYandexStatistics"
import { useVKCampaign } from "@/pages/CreateConnection/composables/useVKCampaign"
import { useStages } from "@/pages/CreateConnection/composables/useStages"
import { useConnectionLoader } from "./composables/useConnectionLoader"
import { useConnectionUpdate } from "./composables/useConnectionUpdate"

const route = useRoute()
const router = useRouter()
const toaster = createToaster({})

const { connection, reloadTabsSource, reloadTabsTarget, saveFilesSource, saveFilesTarget } = useGoogleSheets()
const { selectedYandexAccount, yandexUpload, isYandexDataInputRight, setYandexUpload, setYandexAccount } = useYandexStatistics()
const { selectedVKAccount, vkAdsUpload, isVKDataInputRight, setVKUpload, setVKAccount } = useVKCampaign()
const { createStages, currentStage } = useStages()

const sourceType = ref("GOOGLE_SHEETS")
const targetType = ref("GOOGLE_SHEETS")
const isAccountAttached = ref(true)

const isSourceTypeSelected = ref(true)
const isDataProcessorSelected = ref(true)
const isTargetTypeSelected = ref(true)
const isSourceToggle = ref(false)
const isFilterToggle = ref(false)

const isSourceModalVisible = ref(false)
const isTargetModalVisible = ref(false)

const dataProcessorTypes = ref([
    { dataProcessorType: 'FILTER', label: 'Фильтр' }
])
const selectedProcessorType = ref({})

const { isLoading, loadConnection } = useConnectionLoader(route)

const { isLoadingUpdating, isUpdateButtonActive, updateConnection } = useConnectionUpdate(
    connection,
    yandexUpload,
    selectedYandexAccount,
    vkAdsUpload,
    selectedVKAccount,
    sourceType,
    isYandexDataInputRight,
    isVKDataInputRight,
    toaster,
    route,
    router
)

const isManyTables = computed(() => {
    return connection.value.files_source.length > 1
})

const spreadsheetId = computed(() => {
    if (connection.value.files_source[0]?.self_type === 'GoogleSheetSource') {
        return connection.value.files_source[0].id
    }
    return null
})

const tabId = computed(() => {
    if (connection.value.files_source[0]?.self_type === 'GoogleSheetSource') {
        return connection.value.files_source[0].tab?.sheetId
    }
    return null
})

function selectType(processorType) {
    if (processorType === "SOURCE") {
        isSourceTypeSelected.value = true
    }
    if (processorType === "DESTINATION") {
        isTargetTypeSelected.value = true
    }
}

function changeStep() {
    isSourceTypeSelected.value = false
}

function changeDataProcessorStep() {
    isDataProcessorSelected.value = false
}

function openModal() {
    isSourceModalVisible.value = true
}

function openModalTarget() {
    isTargetModalVisible.value = true
}

function addSource() {
    connection.value.files_source.push({ tabs: [], tab: {} })
}

function selectProcessorType(value) {
    selectedProcessorType.value = value
    isDataProcessorSelected.value = true
}

function openNextStep(stageIndex, skipFilter = false) {
    isSourceToggle.value = false

    const targetStage = createStages.value.find(s => s.index === stageIndex)
    if (targetStage) {
        currentStage.value = targetStage
        currentStage.value.isFocused = true
    }

    if (skipFilter === true) {
        const dataProcessorStage = createStages.value.find(s => s.index === 2)
        if (dataProcessorStage) {
            dataProcessorStage.isSkipped = true
        }
    }
}

function goBack() {
    if (isLoading.value) return
    router.push({
        name: 'FlowView',
        params: { workflow_id: route.params.workflow_id }
    })
}

onBeforeMount(async () => {
    try {
        await loadConnection(
            connection,
            selectedYandexAccount,
            yandexUpload,
            selectedVKAccount,
            vkAdsUpload,
            sourceType,
            setYandexAccount,
            setYandexUpload,
            setVKAccount,
            setVKUpload
        )
    } catch (error) {
        toaster.error('Ошибка загрузки данных соединения')
        console.error(error)
    }

    try {
        await Api.getUserGoogleAccessToken()
    } catch (e) {
        if (e.response?.status === 403) {
            isAccountAttached.value = false
        }
    }
})
</script>

<template>
    <div class="w-full flex h-screen">
        <NavigationBar/>
        <div class="w-full h-full flex flex-col bg-[#DEEAF7]">
            <div class="mt-4 font-bold text-xl mx-auto mb-5 flex items-center justify-start
                        w-1/4 min-w-[calc(100%-40px)] sm:min-w-[400px]">
                <div
                    class="flex items-center justify-center cursor-pointer size-10 hover:bg-blue-200 rounded-full transition-all duration-200"
                    :class="{ 'opacity-50 pointer-events-none': isLoading }"
                    @click="goBack"
                >
                    <BackSvg class="size-6"/>
                </div>
                <div class="ml-10 flex items-center gap-3">
                    <span>Редактирование соединения</span>
                    <LoadingComponent v-if="isLoading" class="size-5"/>
                </div>
            </div>

            <div v-if="isLoading" class="flex-1 flex items-center justify-center">
                <div class="flex flex-col items-center gap-4">
                    <LoadingComponent class="size-12"/>
                    <span class="text-gray-500 text-sm font-medium">Загрузка данных соединения...</span>
                </div>
            </div>

            <template v-else>
                <div class="flex w-full flex-col h-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
                    <div class="sticky top-0 z-10 bg-[#DEEAF7] w-full">
                        <ConnectionStage
                            class="my-4"
                            v-model:stages="createStages"
                            v-model:stage="currentStage"
                        />
                    </div>

                    <div class="w-full h-full" v-show="currentStage.stage === 'SOURCE'">
                        <SelectFileType
                            v-if="!isSourceTypeSelected"
                            v-model="sourceType"
                            class="w-full"
                            fileType="SOURCE"
                            @select-type="selectType('SOURCE')"
                        />
                        <div v-else class="w-full">
                            <FilesProcessor
                                v-if="sourceType === 'GOOGLE_SHEETS'"
                                class="w-full"
                                processor-type="SOURCE"
                                v-model:isAccountAttached="isAccountAttached"
                                v-model:files="connection.files_source"
                                v-model:is-toggle="isSourceToggle"
                                @reload-tabs="reloadTabsSource"
                                @open-modal="openModal"
                                @add-source="addSource"
                                @change-step="changeStep"
                                @next-step="openNextStep(2)"
                                @save-files="saveFilesSource"
                            />
                            <YandexDirectStatProcessor
                                v-if="sourceType === 'YANDEX_DIRECT_STAT'"
                                v-model:account="selectedYandexAccount"
                                v-model:startDate="yandexUpload.startDate"
                                v-model:endDate="yandexUpload.endDate"
                                @next-step="openNextStep(3, true)"
                                @change-step="changeStep"
                            />
                            <VKAdsStatProcessor
                                v-if="sourceType === 'VKONTAKTE_CAMPAIGN_STAT'"
                                v-model:account="selectedVKAccount"
                                v-model:startDate="vkAdsUpload.startDate"
                                v-model:endDate="vkAdsUpload.endDate"
                                @next-step="openNextStep(3, true)"
                                @change-step="changeStep"
                            />
                        </div>
                    </div>

                    <div
                        class="flex flex-col items-center w-full"
                        v-show="currentStage.index === 2"
                        v-if="sourceType !== 'YANDEX_DIRECT_STAT' && sourceType !== 'VKONTAKTE_CAMPAIGN_STAT'"
                    >
                        <SelectDataProcessorType
                            class="w-full"
                            v-if="!isDataProcessorSelected"
                            v-model:data-processor-types="dataProcessorTypes"
                            v-model:selected-data-processor-type="selectedProcessorType"
                            @select-filter="selectProcessorType"
                            @skip-step="openNextStep(3)"
                        />
                        <FilterProcessor
                            v-else
                            class="w-full"
                            v-model:conditions="connection.filter.conditions"
                            v-model:condition-type="connection.filter.conditionType"
                            v-model:spreadsheet-id="spreadsheetId"
                            v-model:tab-id="tabId"
                            :is-many-tables="isManyTables"
                            :isToggle="isFilterToggle"
                            @change-step="changeDataProcessorStep"
                            @next-step="openNextStep(3)"
                        />
                    </div>

                    <div
                        class="flex flex-col items-center w-full"
                        v-show="currentStage.index === 3"
                    >
                        <SelectFileType
                            v-if="!isTargetTypeSelected"
                            v-model="targetType"
                            class="w-full"
                            fileType="TARGET"
                            @select-type="selectType('DESTINATION')"
                        />
                        <FileProcessor
                            v-else
                            class="w-full"
                            processor-type="DESTINATION"
                            v-model:file="connection.file_target"
                            v-model:isAccountAttached="isAccountAttached"
                            @reload-tabs="reloadTabsTarget"
                            @open-modal="openModalTarget"
                            @next-step="updateConnection"
                            :is-loading="isLoadingUpdating"
                            :isCreateButtonActive="isUpdateButtonActive"
                            :is-create="false"
                            @save-files="saveFilesTarget"
                            @change-step="changeDataProcessorStep"
                        />
                    </div>
                </div>
            </template>
        </div>
    </div>

    <GoogleSheetSelectModal
        @submit="saveFilesSource"
        :multi-select="true"
        v-model:visibility="isSourceModalVisible"
        v-model:tables="connection.files_source"
    />
    <GoogleSheetSelectModal
        @submit="saveFilesTarget"
        :multi-select="false"
        v-model:visibility="isTargetModalVisible"
        :file="connection.file_target"
    />
</template>

<style scoped>
.half-circle {
    border-radius: 42px 42px 0 0;
    width: 28px;
    height: 16px;
    box-shadow: 6px 6px 8px 0 rgba(0, 0, 0, 0.05);
}
</style>